import React, { useState } from "react";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import InputField from "../../utils/InputField";
import TextAreaField from '../../utils/TextAreaField';
import { validateField } from "../../utils/validation/validation";
import { submitCommunity, editCommunity } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import RegisterCommunityModal from '../Modals/RegisterCommunityModal';
import GoogleMapsWrapper from '../../utils/GoogleMapsWrapper';
import PlaceComponent from '../Address/AddressAutoPop';
import { useLoading } from "../../contexts/LoadingContext";


function CommunityForm({ formSource, community, onHide }) {
  const initialFormValue = {
    communityName: "",
    communityEmail: "",
    communityPhoneNumber: "",
    address: "",
    hasSimplePermit: 0,
    hasSimpleSpect: 0,
    hasSimpleCheck: 0,
  };
  const [formData, setFormData] = useState(community || initialFormValue);
  const [errors, setErrors] = useState({});
  const { showToast } = useToast();
  const [modalShow, setModalShow] = useState(false);
  const { startLoading, stopLoading } = useLoading();

  const handleValidate = (field, error) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: error,
    }));
  };

  const handleChange = (field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
    const error = validateField(field, value);
    handleValidate(field, error);
  };

  const handleCheckboxChange = (field) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: prevData[field] ? 0 : 1,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      const firstErrorField = Object.keys(errors)[0];
      const firstErrorElement = document.getElementById(firstErrorField);
      if (firstErrorElement) {
        firstErrorElement.focus();
      }
      showToast('Please correct the errors in the form before submitting.', 'error');
      return;
    }
    
    try {
      startLoading();
      if (formData.id) {
        await editCommunity(formData);
        showToast('Community updated successfully!', 'success');
      } else {
        await submitCommunity(formData, formSource);
        if (formSource === 'page') {
          setModalShow(true);
        } else {
          showToast('Form submitted successfully!', 'success');
        }
        setFormData(initialFormValue);
        setErrors({});
      }
      if (onHide) {
        onHide(true);
      }
    
    } catch (error) {
      if (error.response && error.response.data) {
        const apiErrors = error.response.data.errors || error.response.data.message;
  
        if (typeof apiErrors === "object") {
          setErrors(apiErrors);
        } else {
          showToast(apiErrors || "An error occurred while submitting the form.", 'error');
        }
      } else {
        showToast("An unexpected error occurred. Please try again.", 'error');
      }
    } finally {
      stopLoading();
    }
  };

  return (
    <>
        <Form onSubmit={handleSubmit}>
        <div className={`form-wrapper mb-5 ${formSource !== 'page' ? "form-modal" : ""}`}>
          <Row>
            {formData.id && <Col md="6">
              <Row className="mb-3">
                <InputField
                  field="status"
                  label="Status"
                  value={formData.status}
                  onChange={handleChange}
                  error={errors.status}
                  type="select"
                  options={["Pending", "Approved", "On Hold", "Rejected"]}
                  as={Col}
                  controlId="communityStatus"
                />
              </Row>
              <Row className="mb-3">
              <TextAreaField
                    field="reason"
                    label="Reason"
                    value={formData.reason}
                    onChange={handleChange}
                    error={errors.notes}
                    as={Col}
                    controlId="community"
                />
              </Row>
            </Col>
            }
            <Col md="6">
            {!formData.id && <div className="form-heading mb-4 text-uppercase fw-bold">
              Community Information
              </div> }
              <Row className="mb-3">
                <InputField
                  field="communityName"
                  label="Community Name"
                  placeholder="Ex. City Planning Department"
                  value={formData.communityName}
                  onChange={handleChange}
                  error={errors.communityName}
                  as={Col}
                  controlId="community"
                />
              </Row>
              <Row className="mb-3">
                <GoogleMapsWrapper>
                  <PlaceComponent
                      field="address"
                      label="Community Address"
                      className="mb-3"
                      value={formData.address || ""}
                      onChange={handleChange}
                      type="text"
                      error={errors.address}
                      controlId="community"
                  />
                </GoogleMapsWrapper> 
              </Row>
              <Row className="mb-3">
                <InputField
                  field="communityEmail"
                  label="Community Email"
                  placeholder="Ex. name@company.com"
                  value={formData.communityEmail}
                  onChange={handleChange}
                  error={errors.communityEmail}
                  type="email"
                  as={Col}
                  controlId="community"
                />
              </Row>

              <Row className="mb-3">
                <InputField
                  field="communityPhoneNumber"
                  label="Community Phone Number"
                  placeholder="+973276418423"
                  value={formData.communityPhoneNumber}
                  onChange={handleChange}
                  error={errors.communityPhoneNumber}
                  as={Col}
                  controlId="community"
                />
              </Row>
            </Col>
            {formSource !== 'page' && <Col md="6">
              <div className="form-heading mb-4 text-uppercase fw-bold">
              Product Access
              </div>

              <div className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="hasSimplePermit"
                  label="SimplePermit"
                  checked={!!formData.hasSimplePermit}
                  onChange={() => handleCheckboxChange('hasSimplePermit')}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  id="hasSimpleSpect"
                  label="SimpleSpect"
                  checked={!!formData.hasSimpleSpect}
                  onChange={() => handleCheckboxChange('hasSimpleSpect')}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  id="hasSimpleCheck"
                  label="SimpleCheck"
                  checked={!!formData.hasSimpleCheck}
                  onChange={() => handleCheckboxChange('hasSimpleCheck')}
                  className="mb-2"
                />
              </div>

            </Col>}
          </Row>
          <div className="d-flex mb-4 justify-content-end mt-4">
            <Button type="submit" className="btn-submit" variant="secondary" size="xl">
              {formData.id ? "Update" : "Submit Form" }
            </Button>
          </div>
        </div>
        </Form>
        <RegisterCommunityModal
          show={modalShow}
          onHide={() => setModalShow(false)}
        />
       </> 
  );
}

export default CommunityForm;
